/**
 * ToolFlyout — popover hosting the variants of a grouped `Tool`. Each row is
 * a `ToolFlyoutRow` (a flattened, text-led counterpart of the button trigger).
 *
 * Built on the shared `Popover`, so it inherits ArrowUp/Down/Enter/Escape
 * navigation and single-character shortcut matching for free. Adjacent
 * variants with different `group` keys render with a thin divider between
 * them.
 */

import { Fragment, type ReactNode, type RefObject } from "react";
import { Popover } from "../Popover.tsx";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_BORDER,
  COLOR_TEXT_DIM,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_TINY,
  TOOL_FLYOUT_GAP,
  TOOL_FLYOUT_MIN_WIDTH,
} from "../tokens.ts";

export type ToolVariant = {
  /** Stable identity; defaults to index when omitted. */
  key?: string;
  /** Icon slot — rendered as-is. Caller sizes it (typically TOOL_FLYOUT_ICON_SIZE). */
  icon: ReactNode;
  /** Row text. */
  label: string;
  /** Right-aligned keyboard hint. Also registers as a single-char shortcut
   *  matched by the shared Popover while the flyout is open. */
  shortcut?: string;
  /** Selected / currently-in-use — paints fg with the accent. */
  selected?: boolean;
  /** Non-interactive. */
  disabled?: boolean;
  /** Row foreground override (axis colors, etc.). */
  color?: string;
  /** Icon-wrapper foreground override. Default: accent when selected, dim otherwise. */
  iconColor?: string;
  /** Adjacent variants with different group strings render a divider between them. */
  group?: string;
  onActivate: () => void;
};

export function ToolFlyout({
  anchorRef,
  open,
  onClose,
  variants,
  placement,
  dragHighlightIndex,
  minWidth,
}: {
  anchorRef: RefObject<HTMLButtonElement | null>;
  open: boolean;
  onClose: () => void;
  variants: readonly ToolVariant[];
  placement: "below" | "above" | "right" | "left";
  dragHighlightIndex: number | null;
  /** Override the default flyout minimum width. */
  minWidth?: number;
}) {
  return (
    <Popover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      placement={placement}
      gap={TOOL_FLYOUT_GAP}
      style={{
        padding: "4px 0",
        minWidth: minWidth ?? TOOL_FLYOUT_MIN_WIDTH,
      }}
    >
      {variants.map((v, i) => {
        const prev = i > 0 ? variants[i - 1] : null;
        const isGroupStart =
          prev != null && v.group != null && prev.group !== v.group;
        return (
          <Fragment key={v.key ?? i}>
            {isGroupStart && <FlyoutDivider />}
            <ToolFlyoutRow
              variant={v}
              index={i}
              highlighted={dragHighlightIndex === i}
              onActivate={() => {
                v.onActivate();
                onClose();
              }}
            />
          </Fragment>
        );
      })}
    </Popover>
  );
}

function FlyoutDivider() {
  return (
    <div
      style={{
        height: 1,
        margin: "4px 8px",
        background: COLOR_BORDER,
      }}
    />
  );
}

function ToolFlyoutRow({
  variant,
  index,
  highlighted,
  onActivate,
}: {
  variant: ToolVariant;
  index: number;
  highlighted: boolean;
  onActivate: () => void;
}) {
  const { icon, label, shortcut, selected, disabled, color, iconColor } =
    variant;

  const resolvedFg = highlighted
    ? COLOR_ACCENT
    : (color ?? (selected ? COLOR_ACCENT : COLOR_TEXT_SECONDARY));

  const resolvedIconColor =
    iconColor ?? (selected || highlighted ? COLOR_ACCENT : COLOR_TEXT_DIM);

  return (
    <button
      data-popover-item
      data-popover-shortcut={shortcut ? shortcut.toLowerCase() : undefined}
      data-belt-index={index}
      data-belt-disabled={disabled ? "" : undefined}
      onClick={disabled ? undefined : onActivate}
      disabled={disabled}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
        padding: "6px 12px",
        border: "none",
        background: highlighted ? COLOR_ACTIVE_BG : "transparent",
        color: resolvedFg,
        fontSize: FONT_SIZE_BODY,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.45 : 1,
        textAlign: "left" as const,
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          color: resolvedIconColor,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {shortcut && (
        <span style={{ fontSize: FONT_SIZE_TINY, color: COLOR_TEXT_DIM }}>
          {shortcut}
        </span>
      )}
    </button>
  );
}
